import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, Trash2, Pencil, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr, fmtDate } from "../api";
import { useAuth } from "../AuthContext";
import { Btn, ConfirmDialog, Modal, Field, inputCls } from "../components";

const MediaLibrary = () => {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const fileRef = useRef(null);

  const load = () => adminApi.get("/media").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await adminApi.post("/media", fd);
      toast.success(`${r.data.length} image(s) uploaded & optimized`);
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await adminApi.patch(`/media/${edit.id}`, { title: edit.title, alt: edit.alt, caption: edit.caption });
      toast.success("Image details saved");
      setEdit(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await adminApi.delete(`/media/${del.id}`);
      toast.success("Image deleted");
      setDel(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
      setDel(null);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-media-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Media Library</h1>
          <p className="mt-1 text-sm text-slate-500">JPG, PNG or WebP up to 15 MB. Images are automatically resized and compressed for the web.</p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" multiple className="hidden" onChange={upload} data-testid="media-upload-input" />
          <Btn onClick={() => fileRef.current?.click()} disabled={busy} data-testid="media-upload-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload Images
          </Btn>
        </div>
      </div>

      {items === null ? (
        <p className="mt-10 text-center text-slate-400">Loading…</p>
      ) : items.length === 0 ? (
        <div className="mt-8 bg-white border-2 border-dashed border-slate-300 rounded-sm py-16 text-center text-slate-400">
          <ImageOff size={32} className="mx-auto mb-3" />
          <p className="text-sm">No uploaded images yet. Project photos uploaded here can be used across the website.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {items.map((m) => (
            <div key={m.id} className="bg-white border border-slate-200 rounded-sm overflow-hidden group" data-testid={`media-item-${m.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                <img src={m.thumb_url} alt={m.alt || m.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-3">
                <p className="text-xs font-semibold text-slate-800 truncate">{m.title}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{m.width}×{m.height} · {fmtDate(m.created_at).split(",")[0]}</p>
                <div className="mt-2 flex items-center gap-1">
                  <button onClick={() => setEdit({ ...m })} className="p-1.5 text-slate-400 hover:text-[#C5221F]" title="Edit details" data-testid={`media-edit-${m.id}`}><Pencil size={13} /></button>
                  {user.role === "super_admin" && (
                    <button onClick={() => setDel(m)} className="p-1.5 text-slate-400 hover:text-red-600" title="Delete" data-testid={`media-delete-${m.id}`}><Trash2 size={13} /></button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!edit} onClose={() => setEdit(null)} title="Image Details" wide>
        {edit && (
          <form onSubmit={saveEdit} className="grid sm:grid-cols-2 gap-6">
            <img src={edit.url} alt="" className="w-full aspect-video object-contain bg-slate-100 border border-slate-200" />
            <div className="space-y-4">
              <Field label="Title"><input className={inputCls} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="media-title-input" /></Field>
              <Field label="ALT Text" hint="Describes the image for search engines & screen readers.">
                <input className={inputCls} value={edit.alt} onChange={(e) => setEdit({ ...edit, alt: e.target.value })} data-testid="media-alt-input" />
              </Field>
              <Field label="Caption"><input className={inputCls} value={edit.caption} onChange={(e) => setEdit({ ...edit, caption: e.target.value })} data-testid="media-caption-input" /></Field>
              <div className="flex justify-end gap-3">
                <Btn variant="ghost" type="button" onClick={() => setEdit(null)}>Cancel</Btn>
                <Btn type="submit" data-testid="media-save-btn">Save</Btn>
              </div>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog open={!!del} title="Delete this image?" message={`"${del?.title}" will be permanently deleted. Images currently used by projects or pages cannot be deleted.`} onConfirm={doDelete} onClose={() => setDel(null)} busy={busy} />
    </div>
  );
};

export default MediaLibrary;
